import { z } from 'zod';
/**
 * Chaves no estilo do Microsoft Graph (`User.Read`, `Mail.Send`):
 * `Recurso.Ação`, o recurso no plural e em inglês. `Read` vê, `Write` cria e
 * altera (e exige `Read`); ações próprias quando o efeito é outro
 * (`Messages.Send`, `Routines.Run`).
 */
const PERMISSION_KEY = /^[A-Z][A-Za-z]*\.[A-Z][A-Za-z]*$/;
/** O recurso de cada permissão do kit, como a tela o agrupa. */
export const KIT_RESOURCES = {
    Users: 'Usuários',
    Roles: 'Perfis',
    Permissions: 'Permissões',
    Messages: 'Mensagens',
    Deliveries: 'Disparos',
    Routines: 'Rotinas',
    Diagnostics: 'Diagnóstico',
    TeamsUser: 'Teams',
};
/**
 * Permissões que o próprio kit exige nas rotas e telas que entrega. Toda
 * aplicação as tem; as dela entram por `definePermissions`.
 *
 * Para integrações (M2M), cada chave também é o `value` de um app role do
 * app no Entra ID (appPackage/aad.manifest.json da aplicação).
 */
export const KIT_PERMISSIONS = {
    'Users.Read': {
        title: 'Ver usuários',
        description: 'Vê quem tem acesso, com quais perfis, o histórico e se o app está no Teams de cada um.',
        // Os perfis de cada pessoa aparecem pelo nome.
        requires: ['Roles.Read'],
    },
    'Users.Write': {
        title: 'Gerir usuários',
        description: 'Dá acesso, muda os perfis de cada um, desativa usuários e instala o app no Teams deles.',
        requires: ['Users.Read'],
    },
    'Roles.Read': {
        title: 'Ver perfis',
        description: 'Vê os perfis, o que cada um permite e o histórico deles.',
        // As permissões de cada perfil aparecem com título e descrição.
        requires: ['Permissions.Read'],
    },
    'Roles.Write': {
        title: 'Gerir perfis',
        description: 'Cria perfis, muda o nome, a descrição e as permissões de cada um, e desativa perfis.',
        requires: ['Roles.Read'],
    },
    'Permissions.Read': {
        title: 'Ver permissões',
        description: 'Vê todas as permissões do app, o que cada uma libera e exige.',
    },
    'Permissions.Write': {
        title: 'Editar permissões',
        description: 'Reescreve o título e a descrição das permissões. A chave e o que ela libera vêm do código.',
        requires: ['Permissions.Read'],
    },
    'Messages.Send': {
        title: 'Enviar mensagens',
        description: 'Envia mensagens do bot a qualquer pessoa, pelo e-mail, e reenvia disparos.',
    },
    'Deliveries.Read': {
        title: 'Ver disparos',
        description: 'Vê o histórico de tudo o que o bot enviou, com a mensagem, as tentativas e o motivo das falhas.',
    },
    'Routines.Read': {
        title: 'Ver rotinas',
        description: 'Vê as rotinas, o agendamento, o histórico de execuções e quem receberia cada envio.',
    },
    'Routines.Write': {
        title: 'Configurar rotinas',
        description: 'Liga, desliga e muda o agendamento das rotinas.',
        requires: ['Routines.Read'],
    },
    'Routines.Run': {
        title: 'Executar rotinas',
        description: 'Dispara rotinas fora do agendamento e envia prévias e mensagens de teste.',
        requires: ['Routines.Read'],
    },
    'Diagnostics.Read': {
        title: 'Diagnóstico',
        description: 'Consulta as permissões do Graph concedidas ao bot e se o app está no catálogo.',
    },
    'TeamsUser.Read': {
        title: 'Ver o usuário no Teams',
        description: 'Abre a página Teams › Usuário: o que o Teams e o Entra ID dizem de quem está usando.',
    },
};
/**
 * Quem tem as duas conserta qualquer acesso: dá perfis e muda o que cada um
 * permite. O kit sempre mantém alguém ativo com elas, e ninguém as tira de si.
 */
export const ACCESS_ADMIN_PERMISSIONS = [
    'Users.Write',
    'Roles.Write',
];
export const managesAccess = (permissions) => ACCESS_ADMIN_PERMISSIONS.every((permission) => permissions.includes(permission));
/** @throws Error com todos os problemas do catálogo de uma vez */
function validate(entries, resources) {
    const problems = Object.entries(entries).flatMap(([key, info]) => [
        ...(PERMISSION_KEY.test(key) ? [] : [`${key}: use Recurso.Ação`]),
        ...(Object.hasOwn(resources, key.split('.')[0] ?? '')
            ? []
            : [`${key}: recurso sem nome em resources`]),
        ...(info.requires ?? [])
            .filter((required) => !Object.hasOwn(entries, required))
            .map((required) => `${key}: exige ${required}, que não existe`),
    ]);
    if (problems.length > 0) {
        throw new Error(`Catálogo de permissões inválido:\n${problems.join('\n')}`);
    }
}
/**
 * Monta o catálogo da aplicação. Uma permissão ou um recurso do kit não pode
 * ser redefinido: as rotas do kit dependem do significado deles.
 *
 * @throws Error se uma chave fugir do formato, exigir o que não existe ou
 * redefinir algo do kit
 */
export function definePermissions({ resources, permissions, }) {
    const clash = [
        ...Object.keys(permissions).filter((key) => Object.hasOwn(KIT_PERMISSIONS, key)),
        ...Object.keys(resources).filter((key) => Object.hasOwn(KIT_RESOURCES, key)),
    ];
    if (clash.length > 0) {
        throw new Error(`Já definidos pelo kit: ${clash.join(', ')}`);
    }
    const entries = { ...KIT_PERMISSIONS, ...permissions };
    const allResources = { ...KIT_RESOURCES, ...resources };
    validate(entries, allResources);
    const keys = Object.keys(entries);
    const requires = (permission) => (entries[permission].requires ?? []);
    return {
        entries,
        keys,
        schema: z.enum(keys),
        is: (value) => Object.hasOwn(entries, value),
        resources: allResources,
        resourceOf: (permission) => {
            const resource = permission.split('.')[0] ?? permission;
            return allResources[resource] ?? resource;
        },
        requires,
        missingRequirements: (chosen) => {
            const set = new Set(chosen);
            return chosen
                .map((permission) => ({
                permission,
                missing: requires(permission).filter((needed) => !set.has(needed)),
            }))
                .filter(({ missing }) => missing.length > 0);
        },
    };
}
//# sourceMappingURL=permissions.contract.js.map